import { csrfHeaders } from "../auth/session-api.js";

/* Per-tenant notification policy, over the notification service's admin surface.

   The path sits under `/api/v1/notifications/` because that is the prefix the auth BFF
   proxy forwards to the notification service. The proxy is first-prefix-wins and ends in
   a catch-all `/api/v1/` → playbook-be, so a path outside this prefix would be answered
   by the wrong service. */

const BASE = "/api/v1/notifications/policy";

export type NotificationChannel = "in_app" | "email";

/**
 * What the administrator may do with a channel on one event.
 *
 * `locked` and `unavailable` must not render identically: `locked` means always on and
 * not theirs to change, `unavailable` means the channel cannot carry this event at all.
 * Showing both as one greyed switch tells an administrator that a message they DO
 * receive is one they do not.
 */
export type NotificationChannelControl =
  | "switchable"
  | "locked"
  | "unavailable";

export interface NotificationChannelDecision {
  channel: NotificationChannel;
  enabled: boolean;
  control: NotificationChannelControl;
  /** Written for an administrator. Non-null exactly when `control` is not switchable. */
  reason: string | null;
}

export interface NotificationPolicyEvent {
  /** Canonical type name, never an alias. */
  typeName: string;
  moduleId: string | null;
  class: string;
  /** One decision per channel, in-app first. */
  channels: NotificationChannelDecision[];
}

export class NotificationPolicyApiError extends Error {
  constructor(
    message: string,
    readonly code?: string,
  ) {
    super(message);
    this.name = "NotificationPolicyApiError";
  }
}

async function request(path: string, init: RequestInit = {}): Promise<unknown> {
  const response = await fetch(path, {
    ...init,
    credentials: "include",
    headers: {
      Accept: "application/json",
      ...(init.body ? { "Content-Type": "application/json" } : {}),
      ...csrfHeaders(),
      ...init.headers,
    },
  });
  if (!response.ok) {
    const body = (await response.json().catch(() => null)) as {
      code?: string;
      message?: string;
    } | null;
    throw new NotificationPolicyApiError(
      body?.message ?? body?.code ?? `Request failed (${response.status})`,
      body?.code,
    );
  }
  /* Same guard the failure path has: nothing enforces "200 implies JSON" at runtime, and
     an ingress answering 200 with an HTML page would otherwise put the engine's
     `Unexpected token '<'` in front of an administrator. */
  try {
    return await response.json();
  } catch {
    /* The module's OWN error type, like every other failure here. A caller narrowing on
       `NotificationPolicyApiError` to read `code` — or to tell an API failure from a
       programming fault — would otherwise miss this one path, and the message alone is
       identical to the shape guard's. */
    throw new NotificationPolicyApiError(
      "The server returned a response we could not read.",
    );
  }
}

export const notificationPolicyQueryKey = ["notifications", "policy"] as const;

/**
 * `request` guarantees only that the body parsed as JSON, so both entry points check the
 * shape they are about to hand to the page.
 *
 * ONE guard for both, because the two failure modes are the same fault and should not
 * read differently to an administrator: an envelope without `events` would otherwise
 * surface as "no notification types registered" — a confident, wrong statement about
 * their configuration — while an event without `channels` would surface as a raw
 * `TypeError` from `channelOf`.
 */
const CONTROLS = new Set(["switchable", "locked", "unavailable"]);
const CHANNELS = new Set(["in_app", "email"]);

function isDecision(value: unknown): value is NotificationChannelDecision {
  const d = value as Record<string, unknown> | null;
  return (
    !!d &&
    typeof d === "object" &&
    CHANNELS.has(d["channel"] as string) &&
    typeof d["enabled"] === "boolean" &&
    CONTROLS.has(d["control"] as string) &&
    (d["reason"] === null || typeof d["reason"] === "string")
  );
}

function asPolicyEvent(value: unknown): NotificationPolicyEvent {
  const event = value as {
    typeName?: unknown;
    moduleId?: unknown;
    class?: unknown;
    channels?: unknown;
  } | null;
  /* The DECISIONS are checked, not merely the array around them. Stopping at
     `Array.isArray(channels)` let `{channels:[null]}` through, and the crash simply moved
     one step later into `channelOf` — a raw TypeError in front of an administrator
     instead of the sentence this guard exists to produce. Every field the surface reads
     is checked here, because a half-valid decision renders a switch in a state the
     server never described.

     `moduleId` and `class` are checked for the same reason, not for completeness:
     `moduleId` is passed to `labelForModule()`, whose fallback returns the value
     UNCHANGED, and the result is rendered as a `Badge` child. An object there reaches
     React as a child and takes down the whole section with "Objects are not valid as a
     React child" — the exact crash this guard exists to convert into a sentence. */
  if (
    !event ||
    typeof event.typeName !== "string" ||
    !(event.moduleId === null || typeof event.moduleId === "string") ||
    typeof event.class !== "string" ||
    !Array.isArray(event.channels) ||
    !event.channels.every(isDecision)
  ) {
    throw new NotificationPolicyApiError(
      "The server returned notification settings we could not read.",
    );
  }
  return event as NotificationPolicyEvent;
}

export async function listNotificationPolicy(): Promise<
  NotificationPolicyEvent[]
> {
  const body = await request(BASE);
  const events = (body as { events?: unknown } | null)?.events;
  if (!Array.isArray(events)) {
    throw new NotificationPolicyApiError(
      "The server returned notification settings we could not read.",
    );
  }
  return events.map(asPolicyEvent);
}

export async function setNotificationPolicyChannel(
  typeName: string,
  channel: NotificationChannel,
  enabled: boolean,
): Promise<NotificationPolicyEvent> {
  return asPolicyEvent(
    await request(`${BASE}/${encodeURIComponent(typeName)}`, {
      method: "PUT",
      body: JSON.stringify({ channel, enabled }),
    }),
  );
}

/** The decision for one channel, or undefined if the server omitted it. */
export function channelOf(
  event: NotificationPolicyEvent,
  channel: NotificationChannel,
): NotificationChannelDecision | undefined {
  return event.channels.find((c) => c.channel === channel);
}
